import { addMonthsToDate, addYearsToDate, formatDate } from '@travelata-libs/utils';
import { computed, type InjectionKey, provide, inject, ref, type Ref, watch } from 'vue';
import {
  type CalendarSystemModelValue,
  type CalendarModelValue,
  MonthName,
  type DateTimestamp,
  type DateISO,
} from '../Calendar.types';
import { convertModelValueToTimestamp, convertModelValueToTextString } from '../utils/date';
import { getCalendarEvents } from '../utils/events';

const CALENDAR_KEY: InjectionKey<ReturnType<typeof useCalendarModel>> = Symbol('SelectionModel');

export function useCalendarModel(range: Readonly<Ref<boolean>>, model: Readonly<Ref<CalendarModelValue>>) {
  const systemModelValue = ref<CalendarSystemModelValue>(convertModelValueToTimestamp(range.value, model.value));
  const displayedDate = ref<DateTimestamp>(Date.now());
  const calendarEvents = ref(getCalendarEvents(range.value));

  const updateDisplayedDateByModelValue = () => {
    if (!systemModelValue.value || !systemModelValue.value.start) return;

    displayedDate.value = systemModelValue.value.start;
  };

  const submitText = computed(() => convertModelValueToTextString(range.value, systemModelValue.value));

  const getYear = (year: DateTimestamp | DateISO | Date): number => {
    return Number(formatDate(year, 'yyyy'));
  };

  const defaultMinYear = getYear(addYearsToDate(Date.now(), -90));
  const defaultMaxYear = getYear(addYearsToDate(Date.now(), 10));

  const getYearsRange = (minDate: Date | null = null, maxDate: Date | null = null) => {
    const minimumYear = minDate ? getYear(minDate) : defaultMinYear;
    const maximumYear = maxDate ? getYear(maxDate) : defaultMaxYear;

    return Array.from({ length: maximumYear - minimumYear + 1 }, (_, i) => minimumYear + i);
  };

  const getRange = (minDate: Date | null = null, maxDate: Date | null = null) => {
    return getYearsRange(minDate, maxDate).flatMap((year) =>
      Object.values(MonthName).map((month) => `${month} ${year}`),
    );
  };

  const displayedMonth = computed(() => {
    const month = formatDate(displayedDate.value, 'LLLL');

    return month.charAt(0).toUpperCase() + month.slice(1);
  });

  const displayedYear = computed(() => getYear(displayedDate.value));
  const displayedMonthYear = computed(() => `${displayedMonth.value} ${displayedYear.value}`);

  const rangeDisplayedMonthYear = computed(() => {
    const monthYear = formatDate(addMonthsToDate(displayedDate.value, 1), 'LLLL yyyy');

    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  });

  watch(
    model,
    (value) => {
      systemModelValue.value = convertModelValueToTimestamp(range.value, value);
      updateDisplayedDateByModelValue();
    },
    { deep: true },
  );

  watch(range, () => {
    calendarEvents.value = getCalendarEvents(range.value);
  });

  const service = {
    systemModelValue,
    displayedDate,
    updateDisplayedDateByModelValue,
    submitText,
    getYearsRange,
    getRange,
    displayedMonth,
    displayedYear,
    displayedMonthYear,
    rangeDisplayedMonthYear,
    calendarEvents,
  };

  provide(CALENDAR_KEY, service);

  return service;
}

export function useCalendar() {
  const value = inject(CALENDAR_KEY);

  if (!value) {
    throw new Error('Call useCalendarModel first!');
  }

  return value;
}
